import { MotionMode, UgcAdProject, UgcAdScene, UgcAdTimeline } from '../../services/api/ugcAdService';
import { UgcAdCompositionScene, UgcAdCtaCard } from './types';
import { CTA_DEFAULT_SECONDS, CTA_MIN_SECONDS } from '../song-ad/sceneTiming';
import { resolveSceneSeconds, toSceneWindows } from '../../components/ugc-ad/sceneTimelineEditor';

/**
 * サーバーのシーン配列 + タイムライン（scene_order/motion）から、
 * UgcAdComposition にそのまま渡せる再生用シーン列を作る純関数（song-ad deriveScenes.ts 踏襲）。
 *
 * song-ad との違いはシーン枠の重みだけ:
 * - song-ad は「歌詞行数」比で楽曲尺を配分する
 * - UGC は構成案生成時にbackendが見積もった `duration_sec` 比で総尺を配分する
 *   （computeSceneWindows は重み比配分＋最小尺保証の純関数なのでそのまま流用できる。
 *   backendのレンダラーも同じ配分を使うこと＝プレビューと書き出しの一致条件）
 */
/**
 * 再生順に並べ、「使わない」シーンを除いた列を返す。
 *
 * 詳細編集の映像トラックもこの順序・除外でシーンを描くため、プレビューと編集UIで
 * ズレが出ないよう単一の実装にしてある。
 */
export function orderedIncludedScenes(
  scenes: UgcAdScene[],
  timeline: UgcAdTimeline | null
): { scene: UgcAdScene; mode: MotionMode }[] {
  const byId = new Map(scenes.map((s) => [s.id, s]));

  const fallbackOrder = [...scenes].sort((a, b) => a.display_order - b.display_order).map((s) => s.id);
  const order = timeline?.scene_order?.length
    ? [...timeline.scene_order.filter((id) => byId.has(id))]
    : [...fallbackOrder];

  fallbackOrder.forEach((id) => {
    if (!order.includes(id)) order.push(id);
  });

  return order
    .map((id) => byId.get(id))
    .filter((s): s is UgcAdScene => !!s)
    .map((s) => ({ scene: s, mode: (timeline?.motion?.[s.id]?.mode ?? s.motion_mode) as MotionMode }))
    .filter((entry) => entry.mode !== 'skip');
}

export function deriveUgcCompositionScenes(
  scenes: UgcAdScene[],
  timeline: UgcAdTimeline | null,
  totalSeconds: number
): UgcAdCompositionScene[] {
  const included = orderedIncludedScenes(scenes, timeline);

  // 詳細編集でシーンの尺を手で変えていればそれを正とする（backend UgcAdProportionalTiming と同じ規則）。
  // 指定が1つも無ければ従来どおり duration_sec 比で totalSeconds を按分する
  const windows = toSceneWindows(
    resolveSceneSeconds(
      included.map((entry) => entry.scene),
      timeline?.scene_durations,
      totalSeconds
    )
  );

  return included.map((entry, index) => ({
    id: entry.scene.id,
    motionMode: entry.mode,
    imageUrl: entry.scene.storyboard.image?.url ?? null,
    videoUrl: entry.scene.clip.video?.url ?? null,
    startSec: windows[index]?.start ?? 0,
    endSec: windows[index]?.end ?? totalSeconds,
  }));
}

/**
 * 末尾の映像にCTAカードを重ねる。シーン枠を縮めるとクリップ内の発話が切れるため、
 * 元の映像・音声・字幕の時刻を保持する（backend UgcAdRenderPlanner と同じ規則）。
 */
export function deriveUgcCtaCard(
  scenes: UgcAdCompositionScene[],
  project: Pick<UgcAdProject, 'cta_enabled' | 'cta' | 'advanced_settings' | 'title'>,
  totalSeconds: number
): { scenes: UgcAdCompositionScene[]; cta: UgcAdCtaCard | null } {
  const enabled = project.cta?.enabled ?? project.cta_enabled;
  if (!enabled || scenes.length === 0) {
    return { scenes, cta: null };
  }

  const text = (
    project.cta?.text?.trim() ||
    project.advanced_settings?.cta?.trim() ||
    project.title?.trim() ||
    ''
  ).slice(0, 40);
  if (!text) {
    return { scenes, cta: null };
  }

  const ctaDuration = Math.min(CTA_DEFAULT_SECONDS, totalSeconds);
  if (ctaDuration < CTA_MIN_SECONDS) {
    return { scenes, cta: null };
  }

  const lastScene = scenes[scenes.length - 1];

  return {
    scenes,
    cta: {
      start: Math.round((totalSeconds - ctaDuration) * 1000) / 1000,
      end: totalSeconds,
      backgroundImageUrl: lastScene.imageUrl,
      logoUrl: project.cta?.logo_url ?? null,
      text,
    },
  };
}
